import type { Stats, Modifier } from '../content/stats';
import type { StatusId, Tag } from '../content/tags';
import type { Action, Flag, StatusApply } from '../content/actions';
import type { HeroId } from '../content/heroes';
import type { ItemInst, Rarity } from '../content/items';

export type EntityId = number;

export const TEAM_MONSTERS = 0;

export interface StatusInst {
  id: StatusId;
  stacks: number;
  /** tick at which it expires */
  until: number;
  /** damage per second per stack (already scaled) or shield amount */
  power: number;
  source: EntityId;
}

export type UnitKind = 'player' | 'mob' | 'boss' | 'revenant' | 'summon';

export interface Unit {
  id: EntityId;
  kind: UnitKind;
  x: number;
  y: number;
  r: number;
  team: number;
  facing: number;
  hp: number;
  maxHp: number;
  stats: Stats;
  statuses: StatusInst[];
  /** absorb pool from the shield status */
  shield: number;
  dead: boolean;
  vx: number;
  vy: number;
  knockX: number;
  knockY: number;
  lastHitBy: EntityId;
  /** tick of last damage taken */
  lastHurt: number;
  /** display name for players/revenants */
  name: string;
  level: number;
  /** cannot be hit or targeted (account pets) */
  untargetable: boolean;
}

export interface SkillSlot {
  id: string;
  /** in-realm rank of the skill (1..5), reset every realm */
  level: number;
  /** chosen evolution path: ring one, then ring two */
  aspects: string[];
  /** actives only: fire on cooldown at the nearest enemy */
  auto?: boolean;
  /** ticks remaining */
  cd: number;
}

export interface TraitSlot {
  id: string;
  level: number;
}

export type DraftOptionType = 'upgrade' | 'keystone' | 'blessing';

export interface DraftOption {
  type: DraftOptionType;
  /** skill / keystone / blessing id */
  id: string;
  /** for upgrade: the skill id */
  skill?: string;
  /** resulting level */
  level?: number;
}

export interface DraftOffer {
  kind: 'skill' | 'keystone';
  options: DraftOption[];
  /** tick at which the first option is auto-picked */
  until: number;
}

export interface ConsumableStack {
  id: string;
  n: number;
}

export interface Equipment {
  weapon: ItemInst;
  armor?: ItemInst;
  charm?: ItemInst;
}

export interface InputState {
  /** movement direction, each -1..1 */
  mx: number;
  my: number;
  /** aim in world coordinates */
  ax: number;
  ay: number;
  fire: boolean;
  /** bitmask of active skill presses this tick (bit i = slot i) */
  skills: number;
  dash: boolean;
  /** consumable slot to use or -1 */
  use: number;
  /** draft option index or -1 */
  pick: number;
  interact: boolean;
  seq: number;
}

export const EMPTY_INPUT: InputState = { mx: 0, my: 0, ax: 0, ay: 0, fire: false, skills: 0, dash: false, use: -1, pick: -1, interact: false, seq: 0 };

export interface PlayerEnt extends Unit {
  kind: 'player';
  accountId: string;
  heroId: HeroId;
  xp: number;
  xpNext: number;
  /** persistent track: rank points buy the kit; the same xp stream fills it slowly */
  rank: number;
  rankXp: number;
  rankXpNext: number;
  /** realm blessings taken this run (ids repeat for stacks); burn on exit */
  blessings: string[];
  /** carried items not worn; lost with the hero */
  backpack: ItemInst[];
  /** tick of the last crowd effect bought against this hero (martyr fame) */
  crowdFedAt: number;
  input: InputState;
  lastSeq: number;
  aim: number;
  actives: SkillSlot[];
  autos: SkillSlot[];
  traits: TraitSlot[];
  keystones: string[];
  equipment: Equipment;
  consumables: (ConsumableStack | null)[];
  draft: DraftOffer | null;
  /** queued draft offers: 'skill' per level, keystone at keystone levels */
  draftQueue: Array<'skill' | 'keystone'>;
  /** extra options granted by scroll_choice */
  draftExtra: number;
  weaponCd: number;
  dashCd: number;
  dashT: number;
  dashDx: number;
  dashDy: number;
  dashCharges: number;
  invulnUntil: number;
  kills: number;
  bossKills: number;
  revenantKills: number;
  damageDealt: number;
  damageTaken: number;
  summons: EntityId[];
  hookCds: Map<string, number>;
  flags: Set<Flag>;
  /** recent movement for 'noMoveShoot' */
  moving: boolean;
  spawnTick: number;
  deathCause: { name: string; kind: string } | null;
  /** last hp fraction seen by lowhp hooks */
  lowHpArmed: boolean;
  /** hero power (base damage scalar) */
  power: number;
  /** random stream for this player (draft rolls) */
  seed: number;
  fameEarned: number;
  interactCd: number;
  /** ticks until respawn allowed (dead player lingering) */
  respawnAt: number;
  /** aggregated hook sources (traits, keystones, aspects, items, hero trait) */
  perks: PerkSource[];
  /** account companion following this hero */
  pet: { id: string; level: number; entityId: EntityId } | null;
  /** lifetime totals restored from a saved hero; account stats count only the difference */
  carried: { kills: number; bossKills: number; revenantKills: number; damageDealt: number };
  /** owner debug cheats; never saved with the hero */
  admin?: { god: boolean; mods: Modifier[] };
}

export interface ItemSave {
  def: string;
  rarity: Rarity;
  affixes: Modifier[];
}

export interface KitSkillSave {
  id: string;
  aspects: string[];
  auto?: boolean;
}

/**
 * A living hero kept between realms in an account slot; dies for good with the run.
 * Only the deliberate part survives: rank, kit, gear. Level, blessings and keystones burn with the realm.
 */
export interface HeroSave {
  v: 2;
  slot: number;
  heroId: HeroId;
  rank: number;
  rankXp: number;
  actives: KitSkillSave[];
  autos: KitSkillSave[];
  traits: { id: string; level: number }[];
  equipment: { weapon: ItemSave; armor?: ItemSave; charm?: ItemSave };
  backpack: ItemSave[];
  kills: number;
  bossKills: number;
  revenantKills: number;
  damageDealt: number;
  /** realm sessions played */
  runs: number;
  playedSec: number;
  createdAt: number;
  updatedAt: number;
}

export interface PerkSource {
  key: string;
  hooks: import('../content/actions').Hook[];
  /** hooks of an aspect fire 'cast' only for their own skill */
  skill?: string;
}

export interface Windup {
  attack: string;
  until: number;
  /** aim point at start of windup */
  tx: number;
  ty: number;
}

export interface MobEnt extends Unit {
  kind: 'mob' | 'boss' | 'revenant' | 'summon';
  def: string;
  tier: number;
  affixes: string[];
  elite: boolean;
  target: EntityId;
  /** per-attack cooldown ticks by attack id */
  atkCd: Map<string, number>;
  windup: Windup | null;
  /** generic timer used by behaviours */
  stateT: number;
  homeX: number;
  homeY: number;
  xpValue: number;
  phase: number;
  /** ids of units summoned by this mob */
  minions: EntityId[];
  /** shielded affix pool */
  affixShield: number;
  affixShieldCd: number;
  /** revenant payload */
  ghost?: GhostSnapshot;
  revActives?: SkillSlot[];
  revWeaponCd?: number;
  /** owner player for summons spawned by a player through a mob-like def */
  owner: EntityId;
  until: number;
  /** wander target */
  wx: number;
  wy: number;
  scaleDamage: number;
  /** account pet flags */
  isPet: boolean;
  petMul: number;
}

export const BASE_HERO_SLOTS = 2;
export const MAX_HERO_SLOTS = 6;
export const BACKPACK_SLOTS = 5;
export const BASE_VAULT_SLOTS = 12;
export const MAX_VAULT_SLOTS = 36;
export const REVENANT_ITEM_CHANCE = 0.05;
/** fame for breaking down a vault item, by rarity */
export const SALVAGE_FAME = [4, 12, 35, 120];

/** Fame price of the next four vault slots. */
export function vaultSlotCost(owned: number): number {
  return 150 + Math.max(0, owned - BASE_VAULT_SLOTS) * 60;
}

export type Visibility = 'hidden' | 'guild' | 'public';

/** Fame price of the next slot; placeholder until slots are sold for real money. */
export function heroSlotCost(owned: number): number {
  return 400 * Math.pow(2, Math.max(0, owned - BASE_HERO_SLOTS));
}

export interface GhostSnapshot {
  id: string;
  accountId: string;
  name: string;
  heroId: HeroId;
  level: number;
  rank: number;
  actives: { id: string; level: number; aspects: string[] }[];
  autos: { id: string; level: number; aspects: string[] }[];
  traits: { id: string; level: number }[];
  keystones: string[];
  weapon: string;
  kills: number;
  /** ticks alive in the run */
  survived: number;
  /** the best piece the hero wore; a slain revenant may drop it once, never to its own account */
  item?: ItemSave;
  itemRolled?: boolean;
}

export interface Projectile {
  id: EntityId;
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  team: number;
  owner: EntityId;
  damage: number;
  tags: Tag[];
  pierce: number;
  bounce: number;
  until: number;
  hits: Set<EntityId>;
  visual: string;
  status?: StatusApply[];
  onHit?: Action;
  onExpire?: Action;
  homing: number;
  targetId: EntityId;
  knockback: number;
  orbit?: { radius: number; angle: number; angVel: number };
  returning: boolean;
  returned: boolean;
  spawnTick: number;
  /** caster power for status scaling */
  power: number;
  critChance: number;
  critMult: number;
  fromSkill?: string;
}

export interface Zone {
  id: EntityId;
  x: number;
  y: number;
  r: number;
  team: number;
  owner: EntityId;
  damage: number;
  tags: Tag[];
  status?: StatusApply[];
  until: number;
  tickEvery: number;
  nextTick: number;
  visual: string;
  pull: number;
  healAllies: number;
  power: number;
  knockback: number;
}

export interface Pickup {
  id: EntityId;
  x: number;
  y: number;
  r: number;
  type: 'item' | 'consumable' | 'heal' | 'xp';
  item?: ItemInst;
  consumable?: string;
  amount: number;
  /** personal loot: only this player sees and takes it (0 = everyone) */
  owner: EntityId;
  until: number;
}

export interface Interactable {
  id: EntityId;
  x: number;
  y: number;
  r: number;
  type: 'shrine' | 'chest' | 'altar';
  used: boolean;
  /** tier of loot */
  tier: number;
  seed: number;
}

export interface Telegraph {
  id: EntityId;
  x: number;
  y: number;
  r: number;
  /** line telegraph: end point */
  x2?: number;
  y2?: number;
  until: number;
  kind: 'circle' | 'line';
}

export type AnyUnit = PlayerEnt | MobEnt;

export interface HitContext {
  source: AnyUnit;
  target: AnyUnit;
  damage: number;
  tags: Tag[];
  crit: boolean;
  fromSkill?: string;
}
